import { Badge, BadgeVariant } from '@/components/ui/badge';
import { cn } from "@/lib/utils";

export type PaymentStatus =
  | 'pending'
  | 'succeeded'
  | 'error'
  | 'refunded'
  | 'declined'
  | 'expired'
  | 'rejected'
  | 'on_hold';
export type PaymentSubStatus =
  | 'authorized'
  | 'waiting_action'
  | 'approved'
  | 'declined'
  | 'captured'
  | 'partially_captured'
  | 'partially_refunded'
  | 'pending_provider_confirm'
  | 'refunded'
  | 'canceled'
  | 'received'
  | 'pending_review'
  | 'error'
  | 'timeout'
  | 'expired'
  | 'rejected'
  | 'unknown'
  | null
  | undefined;

export interface PaymentStatusBadgeProps {
  status: PaymentStatus;
  subStatus?: PaymentSubStatus;
  className?: string;
}

// Get badge variant based on status and subStatus
const getStatusBadgeVariant = (status: PaymentStatus, subStatus?: PaymentSubStatus): BadgeVariant => {
  // Check sub-status first for specific cases (prioritize subStatus for accuracy)
  if (subStatus) {
    // Success states
    if (['authorized', 'approved', 'captured', 'received'].includes(subStatus)) return 'green';

    // Warning/pending states
    if (
      [
        'waiting_action',
        'pending_provider_confirm',
        'pending_review',
        'partially_captured',
        'partially_refunded',
      ].includes(subStatus)
    )
      return 'yellow';

    // Refund/cancellation states
    if (['refunded', 'canceled'].includes(subStatus)) return 'yellow';

    // Error/failure states
    if (['declined', 'error', 'rejected', 'expired', 'timeout'].includes(subStatus)) return 'red';

    // Unknown state
    if (subStatus === 'unknown') return 'gray';
  }

  // Fall back to main status
  if (status === 'succeeded') return 'green';
  if (status === 'pending' || status === 'on_hold') return 'blue';
  if (status === 'refunded') return 'yellow';
  if (status === 'error' || status === 'declined' || status === 'rejected' || status === 'expired') return 'red';
  return 'gray';
};

// Get display label
const getStatusLabel = (status: PaymentStatus, subStatus?: PaymentSubStatus): string => {
  if (subStatus) {
    const subStatusMap: Record<string, string> = {
      authorized: 'Authorized',
      waiting_action: 'Waiting Action',
      approved: 'Approved',
      declined: 'Declined',
      captured: 'Captured',
      partially_captured: 'Partially Captured',
      partially_refunded: 'Partially Refunded',
      pending_provider_confirm: 'Pending Confirmation',
      refunded: 'Refunded',
      canceled: 'Canceled',
      received: 'Received',
      pending_review: 'Pending Review',
      error: 'Error',
      timeout: 'Timeout',
      expired: 'Expired',
      rejected: 'Rejected',
      unknown: 'Unknown',
    };
    if (subStatusMap[subStatus]) return subStatusMap[subStatus];
    // Fallback for unknown subStatus: capitalize and replace underscores
    return subStatus?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown';
  }

  const statusMap: Record<PaymentStatus, string> = {
    pending: 'Pending',
    succeeded: 'Succeeded',
    error: 'Error',
    refunded: 'Refunded',
    declined: 'Declined',
    expired: 'Expired',
    rejected: 'Rejected',
    on_hold: 'On hold',
  };
  return statusMap[status] || status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown';
};

export function PaymentStatusBadge({ status, subStatus, className }: PaymentStatusBadgeProps) {
  return (
    <Badge variant={getStatusBadgeVariant(status, subStatus)} className={cn('min-w-[72px] justify-center', className)}>
      {getStatusLabel(status, subStatus)}
    </Badge>
  );
}
